import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IFlash_ParkerSyncComparisonForLotResponse } from 'src/app/shared/code/interfaces/parcs-interfaces';
import { ParcsDataService } from 'src/app/core/services/data/parcs-data.service';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { ProcessingBannerComponent } from 'src/app/shared/ui/processing-banner/processing-banner.component';
import { IgxGridModule, IgxGridComponent } from '@infragistics/igniteui-angular';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { catchError, firstValueFrom, of, Subscription, switchMap, takeWhile, timer } from 'rxjs';
import { MatSnackBar, MatSnackBarModule, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';
import { IFlashDailyAccountJobStatus } from 'src/app/shared/code/interfaces/parcs-interfaces';

/// <summary>
/// 20261023 :: Ken Moss - Flash/Parker sync comparison grid. Mirrors the AmanoOne sync
/// component's status-toggle/grid/sync-action pattern (see amano-one-sync.component.ts) so the
/// two PARCS sync UIs feel consistent. Comparison data comes from the sandbox comparison endpoint
/// (api-space-ar-engine SandboxController.Flash_ParkerComparisonTest); the "Update All Synced
/// Parkers" / "Sync All Matched Parkers" actions call the real production write endpoint
/// (lanai-api ParcsController.Flash_UpdateExistingAccountFromSpaceArParker), which pushes the
/// current spACE-AR Parker data to Flash — the same write path the full "Update Parker" screen uses.
/// </summary>
@Component({
  selector: 'app-flash-sync-home',
  standalone: true,
  imports: [
    CommonModule,
    ProcessingBannerComponent,
    IgxGridModule,
    IgxGridComponent,
    MatButtonToggleModule,
    MatIconModule,
    MatCardModule,
    MatButtonModule,
    ClipboardModule,
    MatSnackBarModule,
  ],
  templateUrl: './flash-sync-home.component.html',
  styleUrl: './flash-sync-home.component.scss',
})
export class FlashSyncHomeComponent implements OnInit {
  syncData: IFlash_ParkerSyncComparisonForLotResponse[] = [];
  displaySyncData: IFlash_ParkerSyncComparisonForLotResponse[] = [];
  loadingMessage: string = undefined;
  selectedSyncStatusToggle: string = 'all';
  syncStatusDescription: string = 'This lists all Active Parkers and Flash accounts/contacts.';
  numberOfParkersFound: number = 0;
  numberOfFlashAccountsFound: number = 0;

  public options = {
    enabled: true,
    copyHeaders: true,
    copyFormatters: true,
    separator: '\t',
  };

  private readonly FLASH_JOB_POLL_INTERVAL_MS = 3000;
  private flashJobPollingSubscription: Subscription | null = null;

  _parcsDataService = inject(ParcsDataService);
  _destroyRefService = inject(DestroyRef);
  _appGeneralStateService = inject(AppGeneralStateService);
  _dialogService = inject(MessageDialogService);
  _snackBar = inject(MatSnackBar);

  ngOnInit(): void {
    this._appGeneralStateService.lotNumberSubject.subscribe({
      next: () => this.onLotNumberChange(),
    });
  }

  onLotNumberChange(): void {
    this.syncData = [];
    this.displaySyncData = [];
  }

  getSyncDataForLot(lotNumber: string): void {
    this.loadingMessage = 'Getting Parker/Flash Sync Data...';
    this._parcsDataService
      .getFlashParcsLotSyncDataForLotNumber(lotNumber)
      .pipe(takeUntilDestroyed(this._destroyRefService))
      .subscribe({
        next: (res) => {
          this.selectedSyncStatusToggle = 'all';
          this.syncData = res;
        },
        error: () => {
          this.loadingMessage = undefined;
        },
        complete: () => {
          this.loadingMessage = undefined;
          this._dialogService.snackBarTopCenterTimed('Parker/Flash Sync Data Loaded Successfully', 3000);
          this.calculateSyncMetrics();
          this.displaySyncData = this.syncData;
        },
      });
  }

  calculateSyncMetrics(): void {
    this.numberOfParkersFound = this.syncData.length > 0 ? this.syncData[0].numberOfParkersFound : 0;
    this.numberOfFlashAccountsFound = this.syncData.length > 0 ? this.syncData[0].numberOfFlashAccountsFound : 0;
  }

  onViewChange(view: string): void {
    this.selectedSyncStatusToggle = view;
    this.displaySyncData = [];
    this._dialogService.snackBarTopCenterTimed('Updating View...', 1500);

    if (this.selectedSyncStatusToggle === 'all') {
      this.syncStatusDescription = 'This lists all Active Parkers and Flash accounts/contacts.';
      this.displaySyncData = this.syncData;
    } else if (this.selectedSyncStatusToggle === 'synced') {
      this.syncStatusDescription =
        'This lists all Parkers and Flash contacts where the RFID tag and Parker name match exactly.';
      this.displaySyncData = this.syncData.filter((item) => item.status == 0);
    } else if (this.selectedSyncStatusToggle === 'ready') {
      this.syncStatusDescription = 'This lists all Parkers and Flash contacts where the RFID tag matches.';
      this.displaySyncData = this.syncData.filter((item) => item.status == 1);
    } else if (this.selectedSyncStatusToggle === 'review') {
      this.syncStatusDescription = 'This lists all Parkers with no matching Flash contact.';
      this.displaySyncData = this.syncData.filter((item) => item.status == 2);
    } else if (this.selectedSyncStatusToggle === 'unable') {
      this.syncStatusDescription = 'This lists Flash accounts/contacts with no matching Parker.';
      this.displaySyncData = this.syncData.filter((item) => item.status == 3);
    } else if (this.selectedSyncStatusToggle === 'applied') {
      this.syncStatusDescription = 'This lists all Parkers and Flash contacts where sync has been applied.';
      this.displaySyncData = this.syncData.filter((item) => item.status == 10);
    } else {
      this.displaySyncData = this.syncData;
    }
  }

  onCopyMessage(message: string): void {
    this._dialogService.snackBarTopCenterTimed(message, 3000);
  }

  async UpdateFlashAccountSynchronous(parker: IFlash_ParkerSyncComparisonForLotResponse): Promise<boolean> {
    try {
      return await firstValueFrom(this._parcsDataService.updateExistingFlashAccountDuringSync(parker.spaceArParkerId));
    } catch {
      // 20261023 :: Ken Moss - Swallow here so one parker cannot abort the bulk sync loop below,
      // mirroring UpdateAmanoOneParcsSynchronous in amano-one-sync.component.ts.
      return false;
    }
  }

  // "Update All Synced Parkers" - exact matches (status 0): RFID + name already match exactly.
  async onSyncNowSyncMatchedParkers(): Promise<void> {
    let failureCount: number = 0;

    for (const parker of this.displaySyncData) {
      this.loadingMessage = `Updating Parker ${parker.parkerFullName} to Flash`;

      const status: boolean = await this.UpdateFlashAccountSynchronous(parker);
      if (status) {
        this.displaySyncData[this.displaySyncData.indexOf(parker)].status = 10;
        this.syncData[this.syncData.indexOf(parker)].status = 10;
      } else {
        failureCount++;
      }
    }
    this.loadingMessage = undefined;
    this._dialogService.snackBarTopCenterTimed(
      failureCount > 0 ? `Sync Process Completed - ${failureCount} parker(s) could not be updated` : 'Sync Process Completed',
      3000,
    );
  }

  // "Sync All Matched Parkers" - RFID-only matches (status 1): RFID matches, name does not match exactly.
  async onSyncNowParkerToParcs(): Promise<void> {
    let failureCount: number = 0;

    for (const parker of this.displaySyncData) {
      this.loadingMessage = `Updating Parker ${parker.parkerFullName} to Flash`;

      const status: boolean = await this.UpdateFlashAccountSynchronous(parker);
      if (status) {
        this.displaySyncData[this.displaySyncData.indexOf(parker)].status = 10;
        this.syncData[this.syncData.indexOf(parker)].status = 10;
      } else {
        failureCount++;
      }
    }
    this.loadingMessage = undefined;
    this._dialogService.snackBarTopCenterTimed(
      failureCount > 0 ? `Sync Process Completed - ${failureCount} parker(s) could not be updated` : 'Sync Process Completed',
      3000,
    );
  }

  // Kicks off the on-demand Flash daily account data pull (lanai-api task/parcs/flash/dailyaccountdata/{lotNumber}).
  // The kickoff endpoint responds HTTP 202 immediately but pre-inserts the job-header row synchronously
  // and returns its FlashDailyDataFetchId, so the actual background completion is confirmed by polling
  // the status endpoint below rather than trusting the 202 itself.
  async onGetCurrentFlashData(): Promise<void> {
    const processingSnackRef = this.openProcessingSnackBar('Updating Flash Account Data');

    try {
      const kickoff = await firstValueFrom(
        this._parcsDataService.flash_GetDailyAccountDataForLot(this._appGeneralStateService.currentLotNumber),
      );

      if (!kickoff.flashDailyDataFetchId) {
        // Should not happen for a single-lot call, but fail safe rather than poll forever with no id.
        processingSnackRef.dismiss();
        this.showFlashDataErrorSnackBar();
        return;
      }

      this.pollFlashDailyAccountJobStatus(kickoff.flashDailyDataFetchId, processingSnackRef);
    } catch (err) {
      processingSnackRef.dismiss();
      this.showFlashDataErrorSnackBar();
    }
  }

  private pollFlashDailyAccountJobStatus(
    flashDailyDataFetchId: string,
    processingSnackRef: MatSnackBarRef<TextOnlySnackBar>,
  ): void {
    this.stopFlashJobPolling();

    this.flashJobPollingSubscription = timer(0, this.FLASH_JOB_POLL_INTERVAL_MS)
      .pipe(
        takeUntilDestroyed(this._destroyRefService),
        switchMap(() => this._parcsDataService.flash_GetDailyAccountDataJobStatus(flashDailyDataFetchId)),
        takeWhile((status) => !status.isComplete, true),
        catchError(() => {
          processingSnackRef.dismiss();
          this.showFlashDataErrorSnackBar();
          return of(null);
        }),
      )
      .subscribe((status: IFlashDailyAccountJobStatus | null) => {
        if (!status || !status.isComplete) {
          return;
        }

        processingSnackRef.dismiss();

        if (status.jobCompletionStatus === 'Success') {
          this._dialogService.snackBarTopCenterTimed('Flash account data update completed', 5000);
        } else {
          this.showFlashDataErrorSnackBar();
        }
      });
  }

  private stopFlashJobPolling(): void {
    this.flashJobPollingSubscription?.unsubscribe();
    this.flashJobPollingSubscription = null;
  }

  private showFlashDataErrorSnackBar(): void {
    this._snackBar.open('Error occurred updating Flash Data', 'Close', {
      horizontalPosition: 'center',
      verticalPosition: 'top',
      duration: 5000,
      panelClass: 'error-snackbar',
    });
  }

  private openProcessingSnackBar(message: string): MatSnackBarRef<TextOnlySnackBar> {
    return this._snackBar.open(message, undefined, {
      duration: undefined,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}
